import { IOption } from '../../../base';
import {
    IClientChat,
    IClientChatHistory,
    IClientQuestionCategory,
    ICompanyAnsweredQuestion,
    ICompanyQuestion,
    IServerChat,
} from '../../../common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IFetchCreateJobQuestionsCategoriesAction,
    IPostCreateCompanyAction,
    IFetchCompanyCategoriesAction,
    IFetchChatsListAction,
    IPostCompanyQuestionAnswerAction,
    IFetchActiveChatHistoryAction,
    ISendCreateChatMessageAction,
    IHandleCreateChatFailedMessageAction,
    ISendChatMessageAction,
    IFetchCompanyQuestionsHistoryAction,
} from './interface';
interface IToolsSlice {
    jobCompanysList: Array<IOption>;
    jobQuestionsCategories: Array<IClientQuestionCategory>;
    chatsList: Array<IClientChat>;
    activeChatQuestions: Array<ICompanyQuestion>;
    activeChatHistory: IClientChatHistory;
    activeChatCurrentQuestion: ICompanyQuestion | null;
    activeChatId: string | null;
    activeCompany: IOption | null;
    activeCompanyQuestionsCategory: IClientQuestionCategory | null;
    activeCompanyQuestionsHistory: Array<ICompanyAnsweredQuestion>;
    activeEditQuestionAnswer: ICompanyAnsweredQuestion | null;
    uiState: {
        isFetchCreateJobCompanysListLoading: boolean;
        isFetchCreateJobQuestionsCategoriesLoading: boolean;
        isPostCreateCompanyLoading: boolean;
        isFetchCompanyCategoriesLoading: boolean;
        isFetchActiveChatQuestionsLoading: boolean;
        isFetchChatsListLoading: boolean;
        isFetchChatHistoryLoading: boolean;
        isFetchCompanyQuestionsHistoryLoading: boolean;
        isAwaitingJobAssistantReply: boolean;
        isPostCompanyQuestionAnswerLoading: boolean;
        createCompanySuccess: boolean;
        isSendChatMessagePending: boolean;
        isSendCreateChatMessagePending: boolean;
    };
}

const initialState: IToolsSlice = {
    jobCompanysList: [],
    jobQuestionsCategories: [],
    chatsList: [],
    activeChatQuestions: [],
    activeChatCurrentQuestion: null,
    activeChatHistory: {
        chatHistory: [],
        generatedContent: [],
    },
    activeChatId: null,
    activeCompany: null,
    activeCompanyQuestionsCategory: null,
    activeCompanyQuestionsHistory: [],
    activeEditQuestionAnswer: null,
    uiState: {
        isFetchCreateJobCompanysListLoading: false,
        isFetchCreateJobQuestionsCategoriesLoading: false,
        isPostCreateCompanyLoading: false,
        isFetchCompanyCategoriesLoading: false,
        isFetchActiveChatQuestionsLoading: false,
        isFetchChatsListLoading: false,
        isFetchChatHistoryLoading: false,
        isFetchCompanyQuestionsHistoryLoading: false,
        isAwaitingJobAssistantReply: false,
        isPostCompanyQuestionAnswerLoading: false,
        createCompanySuccess: false,
        isSendChatMessagePending: false,
        isSendCreateChatMessagePending: false,
    },
};

export const sliceName = 'tools';
export const toolsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        fetchCreateJobCompanysList: (state) => {
            state.uiState.isFetchCreateJobCompanysListLoading = true;
        },
        fetchCreateJobCompanysListDone: (state) => {
            state.uiState.isFetchCreateJobCompanysListLoading = false;
        },
        setCreateJobCompanysList: (
            state,
            action: PayloadAction<Array<IOption>>
        ) => {
            state.jobCompanysList = action.payload;
        },
        fetchCreateJobQuestionsCategories: (
            state,
            _action: PayloadAction<IFetchCreateJobQuestionsCategoriesAction>
        ) => {
            state.uiState.isFetchCreateJobQuestionsCategoriesLoading = true;
        },
        fetchCreateJobQuestionsCategoriesDone: (state) => {
            state.uiState.isFetchCreateJobQuestionsCategoriesLoading = false;
        },
        setCreateJobQuestionsCategories: (
            state,
            action: PayloadAction<Array<IClientQuestionCategory>>
        ) => {
            state.jobQuestionsCategories = action.payload;
        },
        postCreateCompany: (
            state,
            _action: PayloadAction<IPostCreateCompanyAction>
        ) => {
            state.uiState.isPostCreateCompanyLoading = true;
        },
        postCreateCompanyDone: (state) => {
            state.uiState.isPostCreateCompanyLoading = false;
        },
        setCreateCompanySuccess: (state) => {
            state.uiState.createCompanySuccess =
                !state.uiState.createCompanySuccess;
        },
        fetchCompanyCategories: (
            state,
            _action: PayloadAction<IFetchCompanyCategoriesAction>
        ) => {
            state.uiState.isFetchCompanyCategoriesLoading = true;
        },
        fetchCompanyCategoriesDone: (state) => {
            state.uiState.isFetchCompanyCategoriesLoading = false;
        },
        fetchCompanyQuestionsHistory: (
            state,
            _action: PayloadAction<IFetchCompanyQuestionsHistoryAction>
        ) => {
            state.uiState.isFetchCompanyQuestionsHistoryLoading = true;
        },
        fetchCompanyQuestionsHistoryDone: (state) => {
            state.uiState.isFetchCompanyQuestionsHistoryLoading = false;
        },
        setActiveCompanyQuestionsHistory: (
            state,
            action: PayloadAction<Array<ICompanyAnsweredQuestion>>
        ) => {
            state.activeCompanyQuestionsHistory = action.payload;
        },
        setActiveEditQuestionAnswer: (
            state,
            action: PayloadAction<ICompanyAnsweredQuestion | null>
        ) => {
            state.activeEditQuestionAnswer = action.payload;
        },
        setCompanyCategories: (
            state,
            action: PayloadAction<Array<IClientQuestionCategory>>
        ) => {
            state.jobQuestionsCategories = action.payload;
        },
        fetchActiveChatQuestions: (state) => {
            state.uiState.isFetchActiveChatQuestionsLoading = true;
        },
        fetchActiveChatQuestionsDone: (state) => {
            state.uiState.isFetchActiveChatQuestionsLoading = false;
        },
        setActiveChatQuestions: (
            state,
            action: PayloadAction<Array<ICompanyQuestion>>
        ) => {
            state.activeChatQuestions = action.payload;
        },
        fetchChatsList: (
            state,
            _action: PayloadAction<IFetchChatsListAction>
        ) => {
            state.uiState.isFetchChatsListLoading = true;
        },
        fetchChatsListDone: (state) => {
            state.uiState.isFetchChatsListLoading = false;
        },
        setChatsList: (state, action: PayloadAction<Array<IClientChat>>) => {
            state.chatsList = action.payload;
        },
        fetchActiveChatHistory: (
            state,
            _action: PayloadAction<IFetchActiveChatHistoryAction>
        ) => {
            state.uiState.isFetchChatHistoryLoading = true;
        },
        fetchActiveChatHistoryDone: (state) => {
            state.uiState.isFetchChatHistoryLoading = false;
        },
        setActiveChatHistory: (
            state,
            action: PayloadAction<IClientChatHistory>
        ) => {
            state.activeChatHistory = action.payload;
        },
        setActiveChatId: (state, action: PayloadAction<string | null>) => {
            state.activeChatId = action.payload;
        },
        sendCreateChatMessage: (
            state,
            _action: PayloadAction<ISendCreateChatMessageAction>
        ) => {
            state.uiState.isSendCreateChatMessagePending = true;
        },
        handleCreateChatSuccessMessage: (
            state,
            _action: PayloadAction<IServerChat>
        ) => {
            state.uiState.isSendCreateChatMessagePending = false;
        },
        handleCreateChatFailedMessage: (
            state,
            _action: PayloadAction<IHandleCreateChatFailedMessageAction>
        ) => {
            state.uiState.isSendCreateChatMessagePending = false;
        },
        handleChatReplyFromAI: (state, _action: PayloadAction<IServerChat>) => {
            state.uiState.isAwaitingJobAssistantReply = false;
        },
        sendChatMessage: (
            state,
            _action: PayloadAction<ISendChatMessageAction>
        ) => {
            state.uiState.isSendChatMessagePending = true;
            state.uiState.isAwaitingJobAssistantReply = true;
        },
        handleSendChatMessageFailed: (state) => {
            state.uiState.isSendChatMessagePending = false;
            state.uiState.isAwaitingJobAssistantReply = false;
        },
        handleSendChatMessageSuccess: (state) => {
            state.uiState.isSendChatMessagePending = false;
        },
        setActiveCompany: (state, action: PayloadAction<IOption | null>) => {
            state.activeCompany = action.payload;
        },
        setActiveCompanyQuestionsCategory: (
            state,
            action: PayloadAction<IClientQuestionCategory | null>
        ) => {
            state.activeCompanyQuestionsCategory = action.payload;
        },
        postCompanyQuestionAnswer: (
            state,
            _action: PayloadAction<IPostCompanyQuestionAnswerAction>
        ) => {
            state.uiState.isPostCompanyQuestionAnswerLoading = true;
        },
        postCompanyQuestionAnswerDone: (state) => {
            state.uiState.isPostCompanyQuestionAnswerLoading = false;
        },
        setActiveChatCurrentQuestion: (
            state,
            action: PayloadAction<ICompanyQuestion | null>
        ) => {
            state.activeChatCurrentQuestion = action.payload;
        },
        setIsAwaitingJobAssistantReply: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.uiState.isAwaitingJobAssistantReply = action.payload;
        },
    },
});

export const toolsActions = toolsSlice.actions;
export default toolsSlice.reducer;
